import {useAppDensity} from "../app/AppContext";
import {OtsBadge} from "../components/OtsBadge";
import {MetricStrip, type Metric} from "../components/MetricStrip";
import {StatusPill} from "../components/StatusPill";
import {OtsDataTablePrimaryCell} from "../components/OtsDataTableCells";
import {
  OtsDataTable,
  OtsDataTableBody,
  OtsDataTableCell,
  OtsDataTableHead,
  OtsDataTableHeaderCell,
  OtsDataTableRow,
} from "../components/OtsDataTable";
import {DecisionGraph} from "../decision-graph/DecisionGraph";
import {RichMessageText} from "../diagnostics/RichMessageText";
import {compareGaugeQuantity, formatGaugeQuantity, gaugeQuantityStatus} from "./gauge-quantity-display";
import type {GaugeQuantityContribution, GaugeQuantityResult} from "./gauge-quantity-types";

export type GaugeQuantityResultDetailsProps = {
  result: GaugeQuantityResult;
  /** The ticket's gauged quantity to compare with; omit for a preview. */
  storedQuantity?: number | null;
  storedLabel?: string;
  /** Tighter spacing and smaller type for embedded hosts. Defaults to the app density. */
  compact?: boolean;
};

const methods: Record<string, string> = {VOLUME_TABLE: "Volume table", VERTICAL_CYLINDER: "Cylinder"};

const ContributionRow = ({contribution, showEqualized}: {contribution: GaugeQuantityContribution; showEqualized: boolean}) => {
  const unit = contribution.quantityUnit;
  const name = contribution.containerName ?? "Unknown container";
  if (contribution.status !== "CALCULATED")
    return <OtsDataTableRow className="align-top">
      <OtsDataTablePrimaryCell title={name}/>
      <OtsDataTableCell colSpan={showEqualized ? 6 : 5} className="text-[var(--ots-text-muted)]">
        <OtsBadge size="compact" tone={contribution.status === "FAILED" ? "danger" : "neutral"}>
          {contribution.status === "FAILED" ? "Not calculated" : "Excluded"}
        </OtsBadge>
        <RichMessageText className="mt-1 block" message={contribution.message} expanded/>
      </OtsDataTableCell>
    </OtsDataTableRow>;
  return <OtsDataTableRow className="align-top">
    <OtsDataTablePrimaryCell title={name} subtitle={methods[contribution.method ?? ""] ?? contribution.method}/>
    <OtsDataTableCell align="right">{formatGaugeQuantity(contribution.topVolume, unit)}</OtsDataTableCell>
    <OtsDataTableCell align="right">{formatGaugeQuantity(contribution.bottomVolume, unit)}</OtsDataTableCell>
    <OtsDataTableCell align="right">{formatGaugeQuantity(contribution.measuredVolume, unit)}</OtsDataTableCell>
    <OtsDataTableCell align="right">{formatGaugeQuantity(contribution.fillVolume, unit)}</OtsDataTableCell>
    {showEqualized && <OtsDataTableCell align="right">{formatGaugeQuantity(contribution.equalizedVolume, unit)}</OtsDataTableCell>}
    <OtsDataTableCell align="right" className="font-semibold">{formatGaugeQuantity(contribution.quantity, unit)}</OtsDataTableCell>
  </OtsDataTableRow>;
};

/** One calculation: the outcome, each container's arithmetic, and the decision flow that produced it. */
export const GaugeQuantityResultDetails = ({result, storedQuantity, storedLabel = "On ticket", compact: compactProp}: GaugeQuantityResultDetailsProps) => {
  const density = useAppDensity();
  const compact = compactProp ?? density === "compact";
  const status = gaugeQuantityStatus(result.status);
  const contributions = result.contributions ?? [];
  const showEqualized = contributions.some(contribution => contribution.equalizedVolume != null);
  const metrics: Metric[] = [
    {id: "calculated", label: "Calculated", value: formatGaugeQuantity(result.quantity, result.quantityUnit),
      tone: result.quantity != null ? "primary" : "neutral"},
  ];
  if (storedQuantity !== undefined) {
    const comparison = compareGaugeQuantity({result, storedQuantity});
    metrics.push({id: "stored", label: storedLabel, value: formatGaugeQuantity(storedQuantity, result.quantityUnit)},
      {id: "comparison", label: "Comparison", value: comparison.label,
        tone: comparison.kind === "match" ? "success" : comparison.kind === "differs" ? "warning" : "neutral"});
  }
  return <div data-density={compact ? "compact" : "default"} className={`@container ${compact ? "space-y-3" : "space-y-4"}`}>
    <div className="flex flex-wrap items-center gap-2">
      <StatusPill size={compact ? "compact" : "default"} tone={status.tone}>{status.label}</StatusPill>
      <RichMessageText className={`${compact ? "text-xs" : "text-sm"} text-[var(--ots-text)]`} message={result.message}/>
    </div>
    <MetricStrip ariaLabel="Quantities" density={compact ? "compact" : "default"} metrics={metrics}/>
    {contributions.length > 0 &&
      <OtsDataTable aria-label="Container contributions" density={compact ? "compact" : "default"}
        tableClassName={showEqualized ? (compact ? "min-w-[34rem]" : "min-w-[40rem]") : compact ? "min-w-[30rem]" : "min-w-[36rem]"}>
        <OtsDataTableHead>
          <tr>
            <OtsDataTableHeaderCell className="whitespace-nowrap" scope="col">Container</OtsDataTableHeaderCell>
            <OtsDataTableHeaderCell className="whitespace-nowrap" scope="col" align="right">Top volume</OtsDataTableHeaderCell>
            <OtsDataTableHeaderCell className="whitespace-nowrap" scope="col" align="right">Bottom volume</OtsDataTableHeaderCell>
            <OtsDataTableHeaderCell className="whitespace-nowrap" scope="col" align="right">Measured</OtsDataTableHeaderCell>
            <OtsDataTableHeaderCell className="whitespace-nowrap" scope="col" align="right">Incoming</OtsDataTableHeaderCell>
            {showEqualized && <OtsDataTableHeaderCell className="whitespace-nowrap" scope="col" align="right"
              title="Fed through the equalizer pipe by the cluster's ungauged tanks during the pull">Equalized</OtsDataTableHeaderCell>}
            <OtsDataTableHeaderCell className="whitespace-nowrap" scope="col" align="right">Quantity</OtsDataTableHeaderCell>
          </tr>
        </OtsDataTableHead>
        <OtsDataTableBody>{contributions.map(contribution => <ContributionRow key={contribution.readingIndex} contribution={contribution} showEqualized={showEqualized}/>)}</OtsDataTableBody>
      </OtsDataTable>}
    {result.decisionGraph && result.decisionGraph.nodes.length > 0 &&
      <DecisionGraph model={result.decisionGraph} label="How the gauge quantity was derived"
        description="Follow the arrows from the gauge readings to the quantity. Dashed branches show volume methods that were not used; red cards show why a container could not be calculated. An equalized step estimates what the cluster's ungauged tanks fed the pull."/>}
  </div>;
};
